/** @jsxImportSource @emotion/react */

import React from 'react';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as classroomActions } from '@store/slice/classroom-slice';
import { dismissApplicationDownloadByHour } from '@hook/iyagigo/useNotification';
import ToastTemplate from '@component/iyagigo/toast/toast-template';
import ExplanationToast from '@component/iyagigo/toast/notification-toast/explanation-toast';
import ApplicationDownloadToast from '@component/iyagigo/toast/notification-toast/application-download-toast';
import Explanation from '@component/iyagigo/common/explanation';
import TextButton from '@design-system/text-button';
import { ETheme } from '@datatype/type';
import {
  isClozeExplanationToastDataType,
  isKeyExpressionExplanationToastDataType,
  isApplicationDownloadToastDataType,
  isConversationProblemToastDataType,
  isVocabularyExplanationToastDataType,
  TNotificationToast,
} from '@datatype/iyagigo/type';

// explanation toast 하단 버튼. data type에 따라 닫기/보러가기를 렌더하고, 해당 없으면 아무것도 그리지 않는다.
// (conversation → 닫기, key-expression key모드 → 보러가기, 그 외 → null)
const BottomButton: React.FC<{ data: TNotificationToast | null }> = ({ data }) => {
  const dispatch = useTypedDispatch();

  return (
    <>
      {isConversationProblemToastDataType(data) && (
        <TextButton
          theme={ETheme.BROWN}
          size='medium'
          shape='pill'
          width='100%'
          text='닫기'
          handleClick={() => dispatch(modalActions.hideToast())}
        />
      )}
      {isKeyExpressionExplanationToastDataType(data) && data.data.mode === 'key' && (
        <TextButton
          theme={ETheme.BROWN}
          size='medium'
          shape='pill'
          width='100%'
          text='보러가기'
          handleClick={() => {
            // "보러가기" 클릭 → 비디오 자동 재생 플래그 set + 토스트 닫기
            dispatch(classroomActions.storeAutoPlayAuthenticExpression(true));
            dispatch(modalActions.hideToast());
          }}
        />
      )}
    </>
  );
};

const NotificationToast = () => {
  const dispatch = useTypedDispatch();
  const notificationToastData =
    useTypedSelector((state) => state.rootReducer?.modalReducer?.notificationToastData) || null;

  // application-download만 X/배경 클릭 시 1시간 dismiss (다른 toast는 ToastTemplate default 동작 사용)
  const handleApplicationDownloadClose = () => {
    dismissApplicationDownloadByHour();
    dispatch(modalActions.hideToast());
  };
  const onClose = isApplicationDownloadToastDataType(notificationToastData)
    ? handleApplicationDownloadClose
    : undefined;

  return (
    <ToastTemplate onClose={onClose}>
      {/* cloze/vocabulary/conversation/key-expression 해설 토스트는 공통 ExplanationToast 셸 + Explanation 본문으로 통합 */}
      {(isClozeExplanationToastDataType(notificationToastData) ||
        isVocabularyExplanationToastDataType(notificationToastData) ||
        isConversationProblemToastDataType(notificationToastData) ||
        isKeyExpressionExplanationToastDataType(notificationToastData)) && (
        <ExplanationToast
          content={<Explanation data={notificationToastData} />}
          footer={
            isConversationProblemToastDataType(notificationToastData) ||
            (isKeyExpressionExplanationToastDataType(notificationToastData) &&
              notificationToastData.data.mode === 'key') ? (
              <BottomButton data={notificationToastData} />
            ) : undefined
          }
        />
      )}
      {isApplicationDownloadToastDataType(notificationToastData) && <ApplicationDownloadToast />}
    </ToastTemplate>
  );
};

export default NotificationToast;
